import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "phosphor-react";

/**
 * Screenshot Lightbox Modal Component
 * Rendered via React Portal for z-index isolation from parent modals/dialogs.
 * 
 * Props:
 * - isOpen: boolean - Controls visibility
 * - onClose: function - Called when user closes the lightbox
 * - imageUrl: string - Full URL to the image to display
 * - filename: string (optional) - Filename to display in footer
 */
export function ScreenshotLightbox({ isOpen, onClose, imageUrl, filename }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Handle Escape key to close lightbox
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  // Use portal to render at document body level, avoiding dialog z-index conflicts
  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/90 p-4"
      style={{ zIndex: 99999 }}
      onClick={onClose}
      data-testid="screenshot-lightbox-backdrop"
    >
      <div 
        className="relative bg-zinc-900 rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '85vh' }}
      >
        {/* Close button - high z-index and pointer events */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors cursor-pointer"
          style={{ zIndex: 10, pointerEvents: 'auto' }}
          aria-label="Close lightbox"
          data-testid="lightbox-close-btn"
        >
          <X size={24} weight="bold" />
        </button>
        
        {/* Loading spinner */}
        {!imageLoaded && (
          <div className="flex items-center justify-center" style={{ minWidth: '200px', minHeight: '200px' }}>
            <div className="w-10 h-10 border-3 border-[#FF6600] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        {/* Image - key forces re-render when URL changes, resetting load state */}
        <img
          key={imageUrl}
          src={imageUrl}
          alt={filename || 'Screenshot'}
          className="object-contain"
          style={{ 
            maxWidth: '90vw', 
            maxHeight: '80vh',
            display: imageLoaded ? 'block' : 'none' 
          }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />
        
        {/* Footer with filename only */}
        {filename && imageLoaded && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm truncate">{filename}</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
